import type {
  GuestAnalyticsEvent,
  GuestOfferRequestResult,
  GuestOpenOnTvResult,
  GuestResolveResponse,
} from "@shared/types/guest";
import type { GuestRequest } from "@shared/types/guestRequest";

export class GuestApiError extends Error {
  constructor(
    public status: number,
    public code?: string,
    /** Parsed error body - e.g. `{ error, branding }` for a checked-out room. */
    public body?: unknown
  ) {
    super(code ?? `http_${status}`);
  }
}

async function parse(res: Response): Promise<GuestResolveResponse> {
  if (!res.ok) {
    let body: { error?: string } | undefined;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    throw new GuestApiError(res.status, body?.error, body);
  }
  return res.json();
}

/** Resolve a printed room token → content + scoped session cookie. */
export async function resolveToken(
  token: string
): Promise<GuestResolveResponse> {
  return parse(
    await fetch(
      `/api/frontlayer/v1/guest/resolve/${encodeURIComponent(token)}`,
      {
        method: "POST",
        credentials: "same-origin",
      }
    )
  );
}

/** Re-fetch content for an already-established session. */
export async function fetchContent(): Promise<GuestResolveResponse> {
  return parse(
    await fetch(`/api/frontlayer/v1/guest/content`, {
      credentials: "same-origin",
    })
  );
}

/** Ask the room's TV to open `appId`. Never throws - the caller renders the
 *  returned code as a localized message. */
export async function openOnTv(appId: string): Promise<GuestOpenOnTvResult> {
  try {
    const res = await fetch("/api/frontlayer/v1/guest/open-on-tv", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ appId }),
    });
    if (res.ok) return { ok: true };
    let body: { error?: string } | undefined;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    return { ok: false, code: body?.error ?? `http_${res.status}` };
  } catch {
    return { ok: false, code: "network" };
  }
}

/* -------------------------------------------------------------------------- *
 * Room offers (POS-13)
 *
 * Three calls, none of which can throw at the caller: the offers card renders a
 * localized sentence from the returned code, and a card that threw would take
 * the whole companion page with it.
 *
 * **There is no fourth call that waits for news.** The companion session is a
 * stateless cookie with no subscription record, so nothing can push to this
 * phone: the guest sees the row they just created, and `fetchMyRequests` when
 * they next open the page. A poller here would be a fake channel.
 * -------------------------------------------------------------------------- */

/** What this room has asked for. Empty on any failure - a missing list is a
 *  quieter wrong answer than an error card over the whole page. */
export async function fetchMyRequests(): Promise<GuestRequest[]> {
  try {
    const res = await fetch("/api/frontlayer/v1/guest/offers/requests/mine", {
      credentials: "same-origin",
    });
    if (!res.ok) return [];
    const body: unknown = await res.json();
    return Array.isArray(body) ? (body as GuestRequest[]) : [];
  } catch {
    return [];
  }
}

/** Confirm an offer. `idempotencyKey` is minted once per confirm ACTION by the
 *  caller and reused across its own retries - a key minted per attempt is the
 *  same as having none. */
export async function requestOffer(
  offerId: string,
  body: {
    idempotencyKey: string;
    selection?: Array<{ itemId: string; quantity: number }>;
    slotStart?: string;
    partySize?: number;
    note?: string;
  }
): Promise<GuestOfferRequestResult> {
  return postRequest(
    `/api/frontlayer/v1/guest/offers/${encodeURIComponent(offerId)}/request`,
    body
  );
}

/** Withdraw a still-pending request. */
export async function cancelOfferRequest(
  id: string
): Promise<GuestOfferRequestResult> {
  return postRequest(
    `/api/frontlayer/v1/guest/offers/requests/${encodeURIComponent(id)}/cancel`
  );
}

async function postRequest(
  url: string,
  body?: unknown
): Promise<GuestOfferRequestResult> {
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
    const parsed: unknown = await res.json().catch(() => undefined);
    if (res.ok) {
      // A 2xx whose body is not a request row - an intermediary's 204, a
      // truncated response - must not be reported as success. `undefined` here
      // would reach the offers card as a row and throw on the next render,
      // white-screening the whole companion: exactly what these never-throwing
      // wrappers exist to prevent.
      const request = parsed as GuestRequest | undefined;
      if (!request || typeof request.id !== "string")
        return { ok: false, code: "network" };
      return { ok: true, request };
    }
    const code = (parsed as { error?: string } | undefined)?.error;
    return { ok: false, code: code ?? `http_${res.status}` };
  } catch {
    return { ok: false, code: "network" };
  }
}

/** Post analytics events. Uses sendBeacon when available (survives unload). */
export function sendEvents(events: GuestAnalyticsEvent[]): void {
  if (!events.length) return;
  const body = JSON.stringify({ events });
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      "/api/frontlayer/v1/guest/events",
      new Blob([body], { type: "application/json" })
    );
    return;
  }
  fetch("/api/frontlayer/v1/guest/events", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}
